import React, { useState, useEffect } from 'react';
import styles from './AddUserForm.module.css';
import { 
  getModuleListAPI, 
  Module_Response,
  getUserModulePermissionListAPI,
  UserModulePermission_Request,
  UserModulePermission_Response,
} from '../../../services/adminApi';

interface AddUserFormProps {
  onSubmit: (formData: UserFormData, permissions?: Map<number, UserModulePermission_Request>) => void;
  onCancel: () => void;
  initialData?: Partial<UserFormData>;
  isEditing?: boolean;
  userId?: number;
}

export interface UserFormData {
  personName: string;
  userName: string;
  password: string;
  userType: string;
  status: string;
  id?: number;
}

const AddUserForm: React.FC<AddUserFormProps> = ({ onSubmit, onCancel, initialData, isEditing, userId }) => {
  const [modules, setModules] = useState<Module_Response[]>([]);
  const [permissions, setPermissions] = useState<Map<number, UserModulePermission_Request>>(new Map());
  const [formData, setFormData] = useState<UserFormData>({
    personName: '',
    userName: '',
    password: '',
    userType: 'User',
    status: 'Active',
  });

  // Fetch modules from API
  useEffect(() => {
    const loadModules = async () => {
      try {
        const response = await getModuleListAPI();
        if (response.isSuccess && response.data) {
          setModules(response.data);
          
          // Initialize permissions with default values
          const defaultPermissions = new Map<number, UserModulePermission_Request>();
          response.data.forEach((module) => {
            defaultPermissions.set(module.id || 0, {
              moduleId: module.id,
              isView: false,
              isAdd: false,
              isEdit: false,
              isVoid: false,
              isExport: false,
            });
          });
          setPermissions(defaultPermissions);
        }
      } catch (error) {
        console.error('Failed to load modules:', error);
      }
    };

    loadModules();
  }, []);

  // Load existing permissions when editing
  useEffect(() => {
    if (isEditing && userId) {
      const loadPermissions = async () => {
        try {
          const response = await getUserModulePermissionListAPI({ employeeId: userId });
          if (response.isSuccess && response.data) {
            const permissionsMap = new Map<number, UserModulePermission_Request>();
            response.data.forEach((perm: UserModulePermission_Response) => {
              // Convert response to request format
              permissionsMap.set(perm.moduleId || 0, {
                id: perm.id,
                employeeId: typeof perm.employeeId === 'string' ? parseInt(perm.employeeId) : perm.employeeId,
                moduleId: perm.moduleId,
                isView: perm.isView,
                isAdd: perm.isAdd,
                isEdit: perm.isEdit,
                isVoid: perm.isVoid,
                isExport: perm.isExport,
                isActive: perm.isActive,
              });
            });
            setPermissions(permissionsMap);
          }
        } catch (error) {
          console.error('Failed to load user permissions:', error);
        }
      };
      loadPermissions();
    }
  }, [isEditing, userId]);

  // Populate form with initial data when editing
  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
      }));
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePermissionChange = (moduleId: number, permission: keyof Omit<UserModulePermission_Request, 'id' | 'employeeId' | 'moduleId' | 'isActive'>, checked: boolean) => {
    setPermissions((prev) => {
      const updated = new Map(prev);
      const existingPerm = updated.get(moduleId);
      const perm = existingPerm || { 
        moduleId,
        isView: false,
        isAdd: false,
        isEdit: false,
        isVoid: false,
        isExport: false,
      };
      (perm[permission] as boolean) = checked;
      updated.set(moduleId, perm);
      return updated;
    });
  };

  const handleAllPermissions = (moduleId: number, checked: boolean) => {
    setPermissions((prev) => {
      const updated = new Map(prev);
      updated.set(moduleId, {
        moduleId,
        isView: checked,
        isAdd: checked,
        isEdit: checked,
        isVoid: checked,
        isExport: checked,
      });
      return updated;
    });
  };

  const handleColumnHeaderCheckbox = (permissionType: 'isView' | 'isAdd' | 'isEdit' | 'isVoid' | 'isExport', checked: boolean) => {
    setPermissions((prev) => {
      const updated = new Map(prev);
      updated.forEach((perm) => {
        perm[permissionType] = checked;
      });
      return updated;
    });
  };

  const isColumnChecked = (permissionType: 'isView' | 'isAdd' | 'isEdit' | 'isVoid' | 'isExport'): boolean => {
    if (permissions.size === 0) return false;
    return Array.from(permissions.values()).every((perm) => perm[permissionType]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.userName || (!isEditing && !formData.password)) {
      alert('Please fill in all required fields');
      return;
    }
    
    // Debug: Log permissions before submitting
    console.log('=== AddUserForm.handleSubmit ===');
    console.log('Permissions Map size:', permissions.size);
    console.log('Permissions Map entries:');
    permissions.forEach((perm, moduleId) => {
      console.log(`  Module ${moduleId}:`, perm);
    });
    
    // Submit user and permissions together
    onSubmit(formData, permissions);
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      {/* User Details Section */}
      <div className={styles.userDetailsSection}>
        <div className={styles.rowGroup}>
          <div className={styles.field}>
            <label htmlFor="personName" className={styles.label}>
              Person Name
            </label>
            <input
              type="text"
              id="personName"
              name="personName"
              value={formData.personName}
              onChange={handleChange}
              className={styles.input}
              placeholder="Full name"
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="userName" className={styles.label}>
              Username <span className={styles.required}>*</span>
            </label>
            <input
              type="text"
              id="userName"
              name="userName"
              value={formData.userName}
              onChange={handleChange}
              className={styles.input}
              placeholder="admin"
              required
            />
          </div>
        </div>

        <div className={styles.rowGroup}>
          <div className={styles.field}>
            <label htmlFor="password" className={styles.label}>
              Password <span className={styles.required}>*</span>
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              className={styles.input}
              placeholder="••••••"
              required={!isEditing}
            />
          </div>

          <div className={styles.field}>
            <label htmlFor="userType" className={styles.label}>
              Role <span className={styles.required}>*</span>
            </label>
            <select
              id="userType"
              name="userType"
              value={formData.userType}
              onChange={handleChange}
              className={styles.select}
              required
            >
              <option value="User">User</option>
              <option value="Admin">Admin</option>
            </select>
          </div>

          <div className={styles.field}>
            <label htmlFor="status" className={styles.label}>
              Status <span className={styles.required}>*</span>
            </label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              className={styles.select}
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Module Permissions Section */}
      <div className={styles.permissionsSection}>
        <h3 className={styles.permissionsTitle}>Module Permissions</h3>
        <div className={styles.permissionsTableWrapper}>
          <table className={styles.permissionsTable}>
            <thead>
              <tr>
                <th className={styles.moduleHeader}>MODULE</th>
                <th className={styles.permissionHeader}>
                  <div className={styles.headerCheckbox}>
                    <span>VIEW</span>
                    <input
                      type="checkbox"
                      checked={isColumnChecked('isView')}
                      onChange={(e) => handleColumnHeaderCheckbox('isView', e.target.checked)}
                      className={styles.checkbox}
                      title="Check all VIEW permissions"
                    />
                  </div>
                </th>
                <th className={styles.permissionHeader}>
                  <div className={styles.headerCheckbox}>
                    <span>ADD</span>
                    <input
                      type="checkbox"
                      checked={isColumnChecked('isAdd')}
                      onChange={(e) => handleColumnHeaderCheckbox('isAdd', e.target.checked)}
                      className={styles.checkbox}
                      title="Check all ADD permissions"
                    />
                  </div>
                </th>
                <th className={styles.permissionHeader}>
                  <div className={styles.headerCheckbox}>
                    <span>EDIT</span>
                    <input
                      type="checkbox"
                      checked={isColumnChecked('isEdit')}
                      onChange={(e) => handleColumnHeaderCheckbox('isEdit', e.target.checked)}
                      className={styles.checkbox}
                      title="Check all EDIT permissions"
                    />
                  </div>
                </th>
                <th className={styles.permissionHeader}>
                  <div className={styles.headerCheckbox}>
                    <span>VOID</span>
                    <input
                      type="checkbox"
                      checked={isColumnChecked('isVoid')}
                      onChange={(e) => handleColumnHeaderCheckbox('isVoid', e.target.checked)}
                      className={styles.checkbox}
                      title="Check all VOID permissions"
                    />
                  </div>
                </th>
                <th className={styles.permissionHeader}>
                  <div className={styles.headerCheckbox}>
                    <span>EXPORT</span>
                    <input
                      type="checkbox"
                      checked={isColumnChecked('isExport')}
                      onChange={(e) => handleColumnHeaderCheckbox('isExport', e.target.checked)}
                      className={styles.checkbox}
                      title="Check all EXPORT permissions"
                    />
                  </div>
                </th>
                <th className={styles.permissionHeader}>ALL</th>
              </tr>
            </thead>
            <tbody>
              {modules.length === 0 ? (
                <tr>
                  <td colSpan={7} className={styles.noModules}>
                    Loading modules...
                  </td>
                </tr>
              ) : (
                modules.map((module) => {
                  const permission = permissions.get(module.id || 0) || {};
                  return (
                    <tr key={module.id || module.moduleName}>
                      <td className={styles.moduleName}>{module.moduleName}</td>
                      <td>
                        <input
                          type="checkbox"
                          checked={permission.isView || false}
                          onChange={(e) => handlePermissionChange(module.id || 0, 'isView', e.target.checked)}
                          className={styles.checkbox}
                          aria-label={`${module.moduleName} View permission`}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={permission.isAdd || false}
                          onChange={(e) => handlePermissionChange(module.id || 0, 'isAdd', e.target.checked)}
                          className={styles.checkbox}
                          aria-label={`${module.moduleName} Add permission`}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={permission.isEdit || false}
                          onChange={(e) => handlePermissionChange(module.id || 0, 'isEdit', e.target.checked)}
                          className={styles.checkbox}
                          aria-label={`${module.moduleName} Edit permission`}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={permission.isVoid || false}
                          onChange={(e) => handlePermissionChange(module.id || 0, 'isVoid', e.target.checked)}
                          className={styles.checkbox}
                          aria-label={`${module.moduleName} Void permission`}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={permission.isExport || false}
                          onChange={(e) => handlePermissionChange(module.id || 0, 'isExport', e.target.checked)}
                          className={styles.checkbox}
                          aria-label={`${module.moduleName} Export permission`}
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={(permission.isView && permission.isAdd && permission.isEdit && permission.isVoid && permission.isExport) || false}
                          onChange={(e) => handleAllPermissions(module.id || 0, e.target.checked)}
                          className={styles.checkbox}
                          aria-label={`${module.moduleName} All permissions`}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className={styles.actions}>
        <button type="button" onClick={onCancel} className={styles.cancelButton}>
          Cancel
        </button>
        <button type="submit" className={styles.submitButton}>
          {isEditing ? 'Update User' : 'Create User'}
        </button>
      </div>
    </form>
  );
};

export default AddUserForm;
